import React, { ReactNode, useMemo, useState } from "react";

type DragPhase = "idle" | "dragging" | "over" | "drop" | "cancel";

export type DragTheme = {
	state: Record<
		DragPhase,
		{
			list?: string;
			item?: string;
			handle?: string;
			ghost?: string;
			placeholder?: string;
		}
	>;
};

type DragClassNames = {
	list?: string;
	item?: string;
	handle?: string;
	dragged?: string; // item being dragged
	over?: string; // item currently "over"
	placeholder?: string; // insertion marker
};

type DraggableSortListProps<T> = {
	items: T[];
	onChange: (next: T[]) => void;
	renderItem: (item: T, meta: { isDragged: boolean; isOver: boolean, phase:DragPhase, disabled: boolean, index: number  }) => ReactNode;
	keyOf: (item: T) => string;

	horizontal?: boolean; // default: false (vertical)
	theme?: DragTheme;
	classNames?: DragClassNames;

	disabled?: boolean;
};

const move = <T,>(arr: T[], from: number, to: number) => {
	if (from === to) return arr;
	const next = arr.slice();
	const [v] = next.splice(from, 1);
	next.splice(to, 0, v);
	return next;
};

const defaultTheme: DragTheme = {
	state: {
		idle: { list: "", item: "", handle: "", ghost: "", placeholder: "" },
		dragging: { list: "", item: "", handle: "", ghost: "", placeholder: "" },
		over: { list: "", item: "", handle: "", ghost: "", placeholder: "" },
		drop: { list: "", item: "", handle: "", ghost: "", placeholder: "" },
		cancel: { list: "", item: "", handle: "", ghost: "", placeholder: "" },
	},
};

export const DraggableSortList = <T,>({
	items,
	onChange,
	renderItem,
	keyOf,
	horizontal = false,
	theme = defaultTheme,
	classNames,
	disabled = false,
}: DraggableSortListProps<T>) => {
	const [phase, setPhase] = useState<DragPhase>("idle");
	const [dragIndex, setDragIndex] = useState<number | null>(null);
	const [overIndex, setOverIndex] = useState<number | null>(null);

	const axisStyles = useMemo(
		() =>
			horizontal
				? ({ display: "flex", flexDirection: "row"  } as const)
				: ({ display: "flex", flexDirection: "column" } as const),
		[horizontal]
	);

	const phaseCls = theme.state[phase] || theme.state.idle;

	const startDrag = (idx: number) => (e: React.DragEvent) => {
		if (disabled) return;
		setPhase("dragging");
		setDragIndex(idx);
		setOverIndex(idx);
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", String(idx));
		(window as any).reservedDragging = true
		// optional: custom drag preview
	};

	const onEnter = (idx: number) => (e: React.DragEvent) => {
		if (disabled) return;
		e.preventDefault();
		if (dragIndex === null) return;
		setPhase("over");
		setOverIndex(idx);
	};

	const onOver = (idx: number) => (e: React.DragEvent) => {
		if (disabled) return;
		e.preventDefault();
		if (dragIndex === null) return;
		if (overIndex !== idx) setOverIndex(idx);
	};

	const onDrop = (e: React.DragEvent) => {
		if (disabled) return;
		e.preventDefault();
		const from = dragIndex ?? Number(e.dataTransfer.getData("text/plain"));
		const to = overIndex ?? from;

		if (Number.isFinite(from) && Number.isFinite(to) && from >= 0 && to >= 0) {
			const next = move(items, from, to);
			onChange(next);
			setPhase("drop");
		} else {
			setPhase("cancel");
		}

		setDragIndex(null);
		setOverIndex(null);
		// snap back to idle next tick
		requestAnimationFrame(() => setPhase("idle"));
	};

	const onEnd = () => {
		if (disabled) return;
		if (phase !== "drop") setPhase("cancel");
		setDragIndex(null);
		setOverIndex(null);
		requestAnimationFrame(() => setPhase("idle"));
		(window as any).reservedDragging = false
	};

	return (
		<div
			className={`${[classNames?.list, phaseCls.list].filter(Boolean).join(" ")}`}
			style={axisStyles}
			onDragOver={(e) => !disabled && e.preventDefault()}
			onDrop={onDrop}
		>
			{items.map((item, idx) => {
				const isDragged = dragIndex === idx;
				const isOver = overIndex === idx && dragIndex !== null && !isDragged;

				return (
					<div
						key={keyOf ? keyOf(item) : idx}
						className={[
							classNames?.item,
							phaseCls.item,
							isDragged ? classNames?.dragged : "",
							isOver ? classNames?.over : "",
						]
						.filter(Boolean)
						.join(" ")}
						draggable={!disabled}
						onDragStart={startDrag(idx)}
						onDragEnter={onEnter(idx)}
						onDragOver={onOver(idx)}
						onDragEnd={onEnd}
						style={{
							//opacity: isDragged ? 0.5 : 1,
							userSelect: "none",
						}}
					>
						<div
							className={[classNames?.handle, phaseCls.handle].filter(Boolean).join(" ")}
							/*style={{ cursor: disabled ? "not-allowed" : "grab" }}*/
							aria-label="Drag handle"
						/>
						{renderItem(item, { isDragged, isOver, phase,  disabled, index: idx })}


						{isOver && (
							<div
								className={[classNames?.placeholder, phaseCls.placeholder].filter(Boolean).join(" ")}
								style={{
									height: horizontal ? "100%" : 2,
									width: horizontal ? 2 : "100%",
								}}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
};